// Setting → Staff: รายชื่อพนักงานจาก Wazzup (ต้อง login ก่อน)
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { fetchBrandIdsByEmp, settingsKeys } from "@features/settings/api/settings-api";
import { useEnabledBrands } from "@features/settings/api/use-settings";
import { AccessLogos } from "@features/settings/components/access-logos";
import { EmployeeAccessDrawer } from "@features/settings/components/employee-access-drawer";
import { PersonCell } from "@features/settings/components/person-cell";
import {
  byDepartmentThenName,
  fromEmployee,
  type PersonView,
} from "@features/settings/lib/person-view";
import { IconChevronRight, IconSearch } from "@shared/ui/icons";
import { authKeys, fetchEmployees } from "../api/auth-api";
import { ssoLogout } from "../lib/iam-sso";
import { sessionCleared } from "../model/session-slice";
import { StaffLogin } from "./staff-login";

/** ค่าใน dropdown สำหรับ "คนที่ไม่มีแผนก" — ใช้อักขระที่เป็นชื่อแผนกจริงไม่ได้ */
const NO_DEPT = "\u0000none";

export function StaffPage() {
  const session = useAppSelector((s) => s.session.current);
  const dispatch = useAppDispatch();
  const [q, setQ] = useState("");
  /** "" = ทุกแผนก */
  const [dept, setDept] = useState("");
  /** คนที่เปิดแผงสิทธิ์อยู่ — null = ปิด */
  const [picked, setPicked] = useState<PersonView | null>(null);

  const { data, isPending, isError } = useQuery({
    queryKey: authKeys.employees,
    queryFn: () => fetchEmployees(session!.access_token),
    enabled: !!session,
    retry: false,
  });

  /**
   * สิทธิ์ของทุกคนในก้อนเดียว — ถามทีละคนจะเป็น ~200 request ต่อการเปิดหน้าหนึ่งครั้ง
   * โหลดไม่สำเร็จ = ไม่แสดงโลโก้ ไม่ใช่หน้าพัง เพราะไม่ใช่ของหลักของหน้านี้
   */
  const access = useQuery({
    queryKey: settingsKeys.allEmployeeBrands,
    queryFn: fetchBrandIdsByEmp,
    enabled: !!session,
    retry: false,
  });
  const { byId: brandsById } = useEnabledBrands();

  /**
   * ตัวเลือกใน dropdown มาจากข้อมูลทั้งชุด ไม่ใช่ชุดที่กรองแล้ว
   * ไม่งั้นเลือกแผนกหนึ่งแล้วแผนกอื่นจะหายไปจากรายการ กลับไปเลือกใหม่ไม่ได้
   */
  const departments = useMemo(() => {
    const counts = new Map<string, number>();
    let none = 0;
    for (const p of data ?? []) {
      const name = p.departmentName?.trim();
      if (name) counts.set(name, (counts.get(name) ?? 0) + 1);
      else none += 1;
    }
    return {
      list: [...counts.entries()]
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => a.name.localeCompare(b.name, "th")),
      none,
    };
  }, [data]);

  // เรียงตามแผนกแล้วตามชื่อ เหมือนแผงผู้เข้าถึง — คอลัมน์แผนกซ้ายสุดจะไม่มีประโยชน์
  // ถ้ารายชื่อยังเรียงตามลำดับที่ปลายทางส่งมา
  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data ?? [])
      .filter((p) => {
        if (!dept) return true;
        const name = p.departmentName?.trim() || "";
        return dept === NO_DEPT ? !name : name === dept;
      })
      .filter((p) => {
        if (!needle) return true;
        return [
          p.empCode,
          p.empThaiName,
          p.empEngName,
          p.nickName,
          p.positionName,
          p.departmentName,
        ].some((v) => (v || "").toLowerCase().includes(needle));
      })
      .map(fromEmployee)
      .sort(byDepartmentThenName);
  }, [data, q, dept]);

  // RequireSession การันตีว่ามี session ถึงจะมาถึงหน้านี้ · 401 กลางทางถูกจับที่ interceptor
  if (!session) return <StaffLogin />;

  return (
    <>
      <div className="content-head">
        <div>
          <div className="breadcrumb">Setting / Staff</div>
          <h1>Staff</h1>
        </div>
        <div className="head-actions">
          <button type="button" className="btn-outline" onClick={() => dispatch(sessionCleared())}>
            ออกจากระบบ
          </button>
          {
            /* มาทาง SSO ต้องล้าง cookie ที่ IAM ด้วย ไม่งั้นกดเข้าใหม่จะเข้าเงียบ ๆ ทันที
              ล้างของเราก่อนแล้วค่อยไป IAM — ถ้าสลับลำดับ session ฝั่งเราจะค้าง */
            session.via === "sso" && (
              <button
                type="button"
                className="btn-outline"
                onClick={() => ssoLogout(() => dispatch(sessionCleared()))}
              >
                ออกจากระบบทุกระบบ (SSO)
              </button>
            )
          }
        </div>
      </div>

      {isPending ? (
        <div className="panel">
          <p className="muted">กำลังโหลดรายชื่อพนักงาน…</p>
        </div>
      ) : isError ? (
        <div className="panel">
          <p className="muted">โหลดรายชื่อพนักงานไม่สำเร็จ กรุณาลองใหม่อีกครั้ง</p>
        </div>
      ) : (
        <>
          <div className="toolbar">
            <div className="search-box grow-box">
              <IconSearch />
              <input
                type="text"
                placeholder="ค้นหาชื่อ ชื่อเล่น ตำแหน่ง แผนก หรือรหัสพนักงาน..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <label className="camp-select-wrap">
              <span className="muted camp-select-lbl">แผนก</span>
              <select
                className="camp-select"
                value={dept}
                onChange={(e) => setDept(e.target.value)}
              >
                <option value="">ทุกแผนก ({(data ?? []).length.toLocaleString()})</option>
                {departments.list.map((d) => (
                  <option key={d.name} value={d.name}>
                    {d.name} ({d.count.toLocaleString()})
                  </option>
                ))}
                {!!departments.none && (
                  <option value={NO_DEPT}>ไม่ระบุแผนก ({departments.none.toLocaleString()})</option>
                )}
              </select>
            </label>
            <span className="muted">{items.length.toLocaleString()} คน</span>
          </div>
          <div className="mem-list">
            {items.map((v) => (
              <button
                type="button"
                className="mem-row"
                key={v.empCode}
                onClick={() => setPicked(v)}
                title="ตั้งสิทธิ์เข้าถึง Product Group"
              >
                <PersonCell v={v} />
                <AccessLogos brandIds={access.data?.[v.empCode] ?? []} byId={brandsById} />
                <span className="code-chip">{v.empCode}</span>
                <span className="mem-chev">
                  <IconChevronRight />
                </span>
              </button>
            ))}
          </div>
          <EmployeeAccessDrawer person={picked} onClose={() => setPicked(null)} />
        </>
      )}
    </>
  );
}
